import React, { useMemo, type ReactNode } from "react";
import { useEvents, type Event } from "../../../../api/useEvents";
import { useManualFaultEvents } from "../../../../api/useManualFaultEvents";
import { getEventFaultNames } from "../../../../domain/event";
import {
  useDatasetResources,
  useFaultDataset,
  useQuantityDataset,
  type Dataset,
} from "../hook/useDataset";
import { useAvailableResources } from "../hook/useAvailableResources";
import type { BlockDiagramModelT } from "../schema/block/diagram";
import type { BlockPlotModelT } from "../schema/block/plot";
import type { ResourceT } from "../schema/resource";
import { getDatasetRequirements } from "../utils/datasetRequirements";

type DatasetLoaderProps = {
  block: BlockPlotModelT | BlockDiagramModelT;
  projectId: string;
  children: (dataset: Dataset) => ReactNode;
};
type ResolvedDatasetLoaderProps = DatasetLoaderProps & { resources: ResourceT[] };

export function DashboardDatasetLoader(props: DatasetLoaderProps) {
  const availableResources = useAvailableResources({ projectId: props.projectId });
  const resources = useDatasetResources(availableResources, props.block);
  const requirements = getDatasetRequirements(resources, availableResources.fault.identifiers);
  const resolvedProps = { ...props, resources };

  if (requirements.diagnosedEvents) return <EventDatasetLoader {...resolvedProps} />;
  if (requirements.manualEvents) return <ManualEventDatasetLoader {...resolvedProps} />;
  if (requirements.faults) return <FaultDatasetLoader {...resolvedProps} />;
  return <QuantityDatasetLoader {...resolvedProps} />;
}

function QuantityDatasetLoader({
  block,
  projectId,
  resources,
  children,
}: ResolvedDatasetLoaderProps) {
  const { dataset } = useQuantityDataset(block, projectId, resources);
  return <>{children(dataset)}</>;
}

function FaultDatasetLoader({
  block,
  projectId,
  resources,
  children,
  eventsByFault,
}: ResolvedDatasetLoaderProps & { eventsByFault?: Map<string, Event[]> }) {
  const input = useQuantityDataset(block, projectId, resources);
  const dataset = useFaultDataset(input, eventsByFault);
  return <>{children(dataset)}</>;
}

function EventDatasetLoader(props: ResolvedDatasetLoaderProps) {
  // Preserve the existing manual-event cache behavior; impact loading owns its feature flag.
  const events = useEvents(props.projectId, false);
  const eventsByFault = useEventsByFault(events);
  return <FaultDatasetLoader {...props} eventsByFault={eventsByFault} />;
}

function ManualEventDatasetLoader(props: ResolvedDatasetLoaderProps) {
  const manualEvents = useManualFaultEvents(props.projectId, false);
  const events = useMemo<Event[]>(
    () =>
      manualEvents.map(event => ({
        ...event,
        type: "MANUAL",
        failureNames: [event.metadata.manualFault.name],
      })),
    [manualEvents],
  );
  const eventsByFault = useEventsByFault(events);
  return <FaultDatasetLoader {...props} eventsByFault={eventsByFault} />;
}

function useEventsByFault(events: Event[]) {
  return useMemo(() => {
    const map = new Map<string, Event[]>();
    for (const event of events) {
      for (const faultName of getEventFaultNames(event)) {
        const faultEvents = map.get(faultName);
        if (faultEvents) faultEvents.push(event);
        else map.set(faultName, [event]);
      }
    }
    return map;
  }, [events]);
}
